import { useState, useEffect } from 'react';
import axios from 'axios';
import { Ruler, TrendingUp, Clock, Target } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function MaturationTimeline({ childId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/maturation/timeline/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [childId]);

  if (loading) return <div className="animate-pulse h-48 bg-zinc-900/50 rounded-xl" />;
  if (!data) return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5" data-testid="maturation-timeline">
      <p className="text-xs text-zinc-500">Maturation data unavailable</p>
    </div>
  );

  const m = data.maturation;
  const phases = [
    { id: 'pre_phv_early', label: 'Pre-PHV', range: [-4, -1.5] },
    { id: 'pre_phv_approaching', label: 'Approaching', range: [-1.5, -0.5] },
    { id: 'at_phv', label: 'At PHV', range: [-0.5, 0.5] },
    { id: 'post_phv_recent', label: 'Post-PHV', range: [0.5, 2] },
    { id: 'post_phv_mature', label: 'Mature', range: [2, 5] }
  ];

  // Calculate marker position on the timeline (0 to 100%)
  const offset = m?.phv_offset || 0;
  const markerPos = Math.max(0, Math.min(100, ((offset + 4) / 9) * 100));

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="maturation-timeline">
      {/* Header */}
      <div className="p-5 border-b border-zinc-800">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${m?.colour || '#6b7280'}20` }}>
            <Ruler className="h-5 w-5" style={{ color: m?.colour || '#6b7280' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Maturation Timeline</h3>
            <p className="text-xs font-medium" style={{ color: m?.colour }}>{m?.status_label || 'Unknown'}</p>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-2">{m?.description}</p>
      </div>

      {/* Visual Timeline */}
      <div className="px-5 py-6">
        <div className="relative">
          {/* Phase bars */}
          <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
            <div className="flex-[1.5] bg-green-500/40 rounded-l-full" title="Pre-PHV" />
            <div className="flex-1 bg-yellow-500/40" title="Approaching" />
            <div className="flex-1 bg-orange-500/40" title="At PHV" />
            <div className="flex-1 bg-blue-500/40" title="Post-PHV" />
            <div className="flex-[1.5] bg-purple-500/40 rounded-r-full" title="Mature" />
          </div>
          
          {/* Marker */}
          <div 
            className="absolute -top-1 w-5 h-5 rounded-full border-2 border-white bg-zinc-900 shadow-lg transition-all"
            style={{ left: `calc(${markerPos}% - 10px)` }}
          >
            <div className="w-2 h-2 rounded-full bg-white m-auto mt-[3px]" />
          </div>
          
          {/* Labels */}
          <div className="flex mt-2 text-[9px] text-zinc-500 uppercase tracking-wider">
            <div className="flex-[1.5] text-left">Pre-PHV</div>
            <div className="flex-1 text-center">Approaching</div>
            <div className="flex-1 text-center font-semibold text-orange-400">At PHV</div>
            <div className="flex-1 text-center">Post-PHV</div>
            <div className="flex-[1.5] text-right">Mature</div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="px-5 pb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard 
          icon={Clock} 
          label="Years from PHV" 
          value={m?.phv_offset != null ? `${m.phv_offset > 0 ? '+' : ''}${m.phv_offset}` : 'N/A'} 
          colour={m?.colour}
        />
        <MetricCard 
          icon={Target} 
          label="Predicted PHV Age" 
          value={m?.predicted_phv_age ? `${m.predicted_phv_age} yrs` : 'N/A'}
        />
        <MetricCard 
          icon={TrendingUp} 
          label="Growth Rate" 
          value={data.growth_velocity_cm_year ? `${data.growth_velocity_cm_year} cm/yr` : 'N/A'}
          colour={data.growth_velocity_cm_year > 8 ? '#f97316' : '#22c55e'}
        />
        <MetricCard 
          icon={Ruler} 
          label="Predicted Height" 
          value={data.predicted_adult_height_cm ? `${data.predicted_adult_height_cm} cm` : 'N/A'}
          sub={data.height_percentage ? `${data.height_percentage}% achieved` : null}
        />
      </div>

      {/* Training Focus */}
      {m?.training_focus?.length > 0 && (
        <div className="px-5 pb-5">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-2">Recommended Training Focus</p>
          <div className="flex flex-wrap gap-1.5">
            {m.training_focus.map((tip, i) => (
              <span key={i} className="text-[11px] px-2.5 py-1 bg-zinc-800/80 text-zinc-300 rounded-full border border-zinc-700/50">{tip}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, colour, sub }) {
  return (
    <div className="bg-zinc-800/40 rounded-lg p-3 text-center">
      <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: colour || '#a1a1aa' }} />
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold text-white mt-0.5" style={{ color: colour }}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
    </div>
  );
}
